import { format, subDays, subHours, startOfDay, endOfDay } from 'date-fns';

// Mock restaurants
const RESTAURANTS = [
  { id: 'H8VG3ZVN', name: 'Downtown Bistro', location: 'Downtown' },
  { id: 'REST002', name: 'Seaside Grill', location: 'Waterfront' },
  { id: 'REST003', name: 'Mountain View Cafe', location: 'Uptown' }
];

// Real menu items from API (prices in INR)
// Cost set to achieve ~40% profit margin (60% of price)
const MENU_ITEMS = [
  // Cocktails
  { id: 83, name: 'Classic Mojito', category: 'Cocktails', price: 380, cost: 228 },
  { id: 84, name: 'Old Fashioned', category: 'Cocktails', price: 450, cost: 270 },
  { id: 85, name: 'Long Island Iced Tea', category: 'Cocktails', price: 520, cost: 312 },
  { id: 86, name: 'Cosmopolitan', category: 'Cocktails', price: 420, cost: 252 },
  
  // Beer
  { id: 87, name: 'Kingfisher Premium', category: 'Beer', price: 180, cost: 108 },
  { id: 88, name: 'Corona Extra', category: 'Beer', price: 250, cost: 150 },
  { id: 89, name: 'Heineken', category: 'Beer', price: 220, cost: 132 },
  
  // Wine
  { id: 90, name: 'Red Wine - Cabernet', category: 'Wine', price: 480, cost: 288 },
  { id: 91, name: 'White Wine - Chardonnay', category: 'Wine', price: 450, cost: 270 },
  
  // Bar Snacks
  { id: 92, name: 'Loaded Nachos', category: 'Bar Snacks', price: 320, cost: 192 },
  { id: 93, name: 'Buffalo Wings', category: 'Bar Snacks', price: 380, cost: 228 },
  { id: 94, name: 'Crispy Chicken Strips', category: 'Bar Snacks', price: 350, cost: 210 },
  { id: 95, name: 'Loaded Fries', category: 'Bar Snacks', price: 250, cost: 150 },
  { id: 96, name: 'Cheese Quesadilla', category: 'Bar Snacks', price: 280, cost: 168 },
  { id: 97, name: 'Fish & Chips', category: 'Bar Snacks', price: 420, cost: 252 },
  
  // Burgers
  { id: 98, name: 'Classic Cheeseburger', category: 'Burgers', price: 350, cost: 210 },
  { id: 99, name: 'Veggie Burger', category: 'Burgers', price: 300, cost: 180 },
  
  // Mocktails
  { id: 100, name: 'Virgin Mojito', category: 'Mocktails', price: 150, cost: 90 },
  { id: 101, name: 'Fresh Lime Soda', category: 'Mocktails', price: 80, cost: 48 }
];

// Mock staff members with Indian names
const STAFF = {
  waiters: [
    { id: 'W001', name: 'Rajesh Kumar', performance: 0.92 },
    { id: 'W002', name: 'Priya Sharma', performance: 0.88 },
    { id: 'W003', name: 'Amit Patel', performance: 0.85 },
    { id: 'W004', name: 'Sneha Reddy', performance: 0.78 },
    { id: 'W005', name: 'Vikram Singh', performance: 0.95 }
  ],
  chefs: [
    { id: 'C001', name: 'Sandeep Verma', performance: 0.94 },
    { id: 'C002', name: 'Deepika Nair', performance: 0.89 },
    { id: 'C003', name: 'Arjun Mehta', performance: 0.91 },
    { id: 'C004', name: 'Kavita Desai', performance: 0.86 }
  ]
};

// Utility functions
const randomInRange = (min, max) => Math.random() * (max - min) + min;

const randomInt = (min, max) => Math.floor(randomInRange(min, max));

const getBusinessMultiplier = (hour, dayOfWeek) => {
  // Weekend multiplier
  const weekendBonus = [0, 6].includes(dayOfWeek) ? 1.3 : 1.0;
  
  // Hour-based multiplier (lunch and dinner rush)
  let hourMultiplier = 0.3;
  if (hour >= 11 && hour <= 14) hourMultiplier = 1.2; // Lunch rush
  else if (hour >= 18 && hour <= 21) hourMultiplier = 1.5; // Dinner rush
  else if (hour >= 15 && hour <= 17) hourMultiplier = 0.6;
  else if (hour >= 22 || hour <= 10) hourMultiplier = 0.1;
  
  return weekendBonus * hourMultiplier;
};

const generateRealisticTrend = (baseValue, days, volatility = 0.1) => {
  const trend = [];
  let currentValue = baseValue;
  
  for (let i = 0; i < days; i++) {
    const change = randomInRange(-volatility, volatility);
    currentValue = currentValue * (1 + change);
    trend.push(currentValue);
  }
  
  return trend;
};

// Main data generators
class MockDataGenerator {
  // Sales metrics
  generateSalesMetrics(restaurantId, dateRange) {
    const days = dateRange?.days || 7;
    const today = new Date();
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      const dayOfWeek = date.getDay();
      const baseRevenue = 350000; // Base revenue in INR (3.5 Lakhs per day for high-performing restaurant)
      
      // Add daily variation
      const dailyMultiplier = [0, 6].includes(dayOfWeek) ? 1.5 : 1.0; // Higher weekend boost
      const randomVariation = randomInRange(0.90, 1.20);
      const revenue = baseRevenue * dailyMultiplier * randomVariation;
      
      data.push({
        date: format(date, 'yyyy-MM-dd'),
        dayName: format(date, 'EEE'),
        revenue: Math.round(revenue),
        orders: randomInt(150, 400), // More orders for higher revenue
        avgOrderValue: Math.round(revenue / randomInt(150, 400)),
        target: 450000 // 4.5 Lakhs daily target
      });
    }
    
    return data;
  }

  generateHourlySales(date = new Date()) {
    const data = [];
    
    for (let hour = 0; hour < 24; hour++) {
      const dayOfWeek = date.getDay();
      const multiplier = getBusinessMultiplier(hour, dayOfWeek);
      const baseHourlyRevenue = 18000; // Base hourly revenue in INR (increased for Lakhs daily)
      const revenue = baseHourlyRevenue * multiplier * randomInRange(0.8, 1.2);
      
      data.push({
        hour: `${hour}:00`,
        revenue: Math.round(revenue),
        orders: Math.round(revenue / 900), // Higher average order value
        customers: Math.round(revenue / 750)
      });
    }
    
    return data;
  }

  generateTopItems(limit = 10, period = 'week') {
    const items = MENU_ITEMS.map(item => {
      const baseQuantity = randomInt(200, 800); // Significantly more orders for high-performing restaurant
      const quantity = Math.round(baseQuantity * randomInRange(0.7, 1.3));
      const revenue = quantity * item.price;
      const cost = quantity * item.cost;
      const profit = revenue - cost;
      const profitMargin = ((profit / revenue) * 100).toFixed(1);
      
      return {
        id: item.id,
        name: item.name,
        category: item.category,
        quantity,
        revenue: Math.round(revenue),
        profit: Math.round(profit),
        profitMargin: parseFloat(profitMargin),
        rating: randomInRange(4.0, 5.0).toFixed(1),
        trend: randomInRange(-15, 25).toFixed(1) // % change from previous period
      };
    });
    
    // Sort by revenue and return top items
    return items.sort((a, b) => b.revenue - a.revenue).slice(0, limit);
  }

  generateBottomItems(limit = 5) {
    const items = this.generateTopItems(MENU_ITEMS.length);
    return items.sort((a, b) => a.quantity - b.quantity).slice(0, limit);
  }

  generateSalesByChannel() {
    return [
      { channel: 'Dine-in', value: randomInt(1200000, 1800000), percentage: 55, trend: 5 },
      { channel: 'Takeout', value: randomInt(600000, 900000), percentage: 25, trend: -2 },
      { channel: 'Delivery', value: randomInt(450000, 750000), percentage: 20, trend: 12 }
    ];
  }

  // Cost metrics
  generateCostMetrics(dateRange) {
    const revenue = randomInRange(2100000, 3500000); // Revenue in INR (21-35 Lakhs)
    // For 40% profit margin: costs should be 60% of revenue
    const foodCost = revenue * randomInRange(0.22, 0.26); // Lower food cost %
    const laborCost = revenue * randomInRange(0.20, 0.24); // Lower labor cost %
    const otherCosts = revenue * randomInRange(0.12, 0.16); // Other operational costs
    const totalCosts = foodCost + laborCost + otherCosts;
    const grossProfit = revenue - totalCosts;
    const netProfit = grossProfit * randomInRange(0.85, 0.95); // Higher net profit retention
    
    return {
      revenue: Math.round(revenue),
      foodCost: Math.round(foodCost),
      foodCostPercentage: ((foodCost / revenue) * 100).toFixed(1),
      laborCost: Math.round(laborCost),
      laborCostPercentage: ((laborCost / revenue) * 100).toFixed(1),
      primeCost: Math.round(foodCost + laborCost),
      primeCostPercentage: (((foodCost + laborCost) / revenue) * 100).toFixed(1),
      otherCosts: Math.round(otherCosts),
      grossProfit: Math.round(grossProfit),
      grossProfitMargin: ((grossProfit / revenue) * 100).toFixed(1),
      netProfit: Math.round(netProfit),
      netProfitMargin: ((netProfit / revenue) * 100).toFixed(1),
      targets: {
        foodCostTarget: 25,
        laborCostTarget: 22,
        primeCostTarget: 50,
        netProfitTarget: 40 // 40% profit target
      }
    };
  }

  generateCostTrend(days = 14) {
    const data = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      const revenue = randomInRange(280000, 490000); // Revenue in INR (2.8-4.9 Lakhs)
      
      data.push({
        date: format(date, 'MM/dd'),
        foodCost: ((revenue * randomInRange(0.22, 0.26)) / revenue * 100).toFixed(1),
        laborCost: ((revenue * randomInRange(0.20, 0.24)) / revenue * 100).toFixed(1),
        foodCostTarget: 30,
        laborCostTarget: 28
      });
    }
    
    return data;
  }

  generateWasteData() {
    return MENU_ITEMS.slice(0, 7).map(item => ({
      item: item.name,
      quantity: randomInt(2, 15),
      cost: Math.round(randomInt(2, 15) * item.cost * 100) / 100,
      reason: ['Spoilage', 'Over-prep', 'Quality', 'Customer return'][randomInt(0, 4)]
    }));
  }

  // Operational metrics
  generateOperationalMetrics() {
    return {
      tableTurnoverRate: randomInRange(2.5, 4.5).toFixed(1),
      avgTableTurnTime: randomInt(45, 75), // minutes
      avgOrderFulfillmentTime: randomInt(15, 25), // minutes
      avgWaitTime: randomInt(5, 15), // minutes
      orderAccuracyRate: randomInRange(94, 99).toFixed(1), // percentage
      peakHours: ['12:00-14:00', '18:00-21:00'],
      currentOrders: randomInt(15, 45),
      tablesOccupied: randomInt(12, 30),
      totalTables: 35
    };
  }

  generateServiceTimeTrend(days = 7) {
    const data = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      
      data.push({
        date: format(date, 'MM/dd'),
        orderTime: randomInRange(18, 28).toFixed(1),
        serviceTime: randomInRange(8, 15).toFixed(1),
        target: 20
      });
    }
    
    return data;
  }

  // Staff performance metrics
  generateWaiterStats() {
    return STAFF.waiters.map(waiter => {
      const ordersServed = randomInt(80, 200); // More orders for high-performing restaurant
      const salesGenerated = randomInt(120000, 350000); // Sales in INR (1.2L - 3.5L)
      const avgServiceTime = randomInRange(8, 15);
      const customerRating = randomInRange(4.2, 5.0);
      const performance = waiter.performance * randomInRange(0.95, 1.05);
      
      return {
        id: waiter.id,
        name: waiter.name,
        ordersServed,
        salesGenerated: Math.round(salesGenerated),
        avgOrderValue: Math.round(salesGenerated / ordersServed),
        avgServiceTime: avgServiceTime.toFixed(1),
        customerRating: customerRating.toFixed(1),
        tablesServed: randomInt(15, 35),
        performance: (performance * 100).toFixed(0),
        hoursWorked: randomInt(35, 45),
        salesPerHour: Math.round(salesGenerated / randomInt(35, 45))
      };
    }).sort((a, b) => b.salesGenerated - a.salesGenerated);
  }

  generateChefStats() {
    return STAFF.chefs.map(chef => {
      const itemsPrepared = randomInt(80, 200);
      const avgPrepTime = randomInRange(12, 22);
      const accuracy = randomInRange(95, 99.5);
      const performance = chef.performance * randomInRange(0.95, 1.05);
      
      return {
        id: chef.id,
        name: chef.name,
        itemsPrepared,
        avgPrepTime: avgPrepTime.toFixed(1),
        accuracy: accuracy.toFixed(1),
        performance: (performance * 100).toFixed(0),
        hoursWorked: randomInt(40, 50),
        itemsPerHour: Math.round(itemsPrepared / randomInt(40, 50)),
        specialtyDishes: randomInt(15, 40)
      };
    }).sort((a, b) => b.itemsPrepared - a.itemsPrepared);
  }

  generateStaffScheduleCompliance() {
    return {
      scheduledHours: 320,
      actualHours: randomInt(305, 335),
      overtimeHours: randomInt(10, 25),
      absenteeism: randomInRange(2, 8).toFixed(1), // percentage
      onTimeRate: randomInRange(92, 98).toFixed(1) // percentage
    };
  }

  // Customer metrics
  generateCustomerMetrics() {
    return {
      totalCustomers: randomInt(1500, 3000), // More customers for higher revenue
      newCustomers: randomInt(400, 800),
      returningCustomers: randomInt(1100, 2200),
      repeatRate: randomInRange(55, 75).toFixed(1),
      avgSpendPerCustomer: randomInRange(1200, 2000).toFixed(0), // Higher average spend in INR
      customerSatisfactionScore: randomInRange(4.2, 4.8).toFixed(1),
      nps: randomInt(45, 75),
      reviewsCount: randomInt(50, 150),
      avgRating: randomInRange(4.3, 4.9).toFixed(1),
      topComplaint: 'Wait time',
      topPraise: 'Food quality'
    };
  }

  generateCustomerTrafficHeatmap() {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const data = [];
    
    days.forEach((day, dayIndex) => {
      hours.forEach(hour => {
        const multiplier = getBusinessMultiplier(hour, dayIndex);
        const traffic = Math.round(multiplier * 50 * randomInRange(0.8, 1.2));
        
        data.push({
          day,
          hour: `${hour}:00`,
          hourNum: hour,
          traffic,
          intensity: Math.min(traffic / 80, 1) // 0 to 1 for color mapping
        });
      });
    });
    
    return data;
  }

  generateReviewsTrend(days = 30) {
    const data = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      
      data.push({
        date: format(date, 'MM/dd'),
        rating: randomInRange(4.0, 5.0).toFixed(1),
        count: randomInt(2, 12)
      });
    }
    
    return data;
  }

  // Dashboard summary
  generateDashboardSummary(restaurantId) {
    const salesMetrics = this.generateSalesMetrics(restaurantId, { days: 1 })[0];
    const costMetrics = this.generateCostMetrics();
    const operationalMetrics = this.generateOperationalMetrics();
    const customerMetrics = this.generateCustomerMetrics();
    
    return {
      restaurant: RESTAURANTS.find(r => r.id === restaurantId) || RESTAURANTS[0],
      todayRevenue: salesMetrics.revenue,
      todayOrders: salesMetrics.orders,
      revenueChange: randomInRange(-5, 15).toFixed(1),
      ordersChange: randomInRange(-3, 12).toFixed(1),
      avgOrderValue: salesMetrics.avgOrderValue,
      grossProfitMargin: costMetrics.grossProfitMargin,
      netProfitMargin: costMetrics.netProfitMargin,
      foodCostPercentage: costMetrics.foodCostPercentage,
      laborCostPercentage: costMetrics.laborCostPercentage,
      tableTurnoverRate: operationalMetrics.tableTurnoverRate,
      avgServiceTime: operationalMetrics.avgOrderFulfillmentTime,
      customerSatisfaction: customerMetrics.customerSatisfactionScore,
      reviewCount: customerMetrics.reviewsCount,
      activeAlerts: randomInt(0, 5),
      revenueTarget: salesMetrics.target,
      revenueProgress: (salesMetrics.revenue / salesMetrics.target * 100).toFixed(1)
    };
  }

  // Experiments and A/B tests
  generateExperiments() {
    return [
      {
        id: 'EXP001',
        name: 'Happy Hour Pricing',
        startDate: '2024-01-01',
        endDate: '2024-01-14',
        status: 'Completed',
        control: { revenue: 180000, orders: 500 },
        variant: { revenue: 225000, orders: 650 },
        impact: '+25%',
        recommendation: 'Implement',
        confidence: 95
      },
      {
        id: 'EXP002',
        name: 'New Menu Item - Vegan Bowl',
        startDate: '2024-01-08',
        endDate: '2024-01-21',
        status: 'Running',
        orders: 287,
        revenue: 86000,
        rating: 4.6,
        recommendation: 'Keep monitoring',
        confidence: 78
      },
      {
        id: 'EXP003',
        name: 'Table Reservation System',
        startDate: '2023-12-15',
        endDate: '2024-01-15',
        status: 'Completed',
        control: { waitTime: 18, satisfaction: 4.2 },
        variant: { waitTime: 12, satisfaction: 4.6 },
        impact: '-33% wait time',
        recommendation: 'Implement',
        confidence: 92
      }
    ];
  }

  // Alerts and notifications
  generateAlerts() {
    const alerts = [];
    const types = ['warning', 'success', 'info', 'error'];
    
    // Random alerts based on metrics
    if (randomInRange(0, 1) > 0.7) {
      alerts.push({
        type: 'warning',
        title: 'Food Cost Above Target',
        message: 'Food cost is at 32.5%, exceeding target of 30%',
        time: '2 hours ago'
      });
    }
    
    if (randomInRange(0, 1) > 0.6) {
      alerts.push({
        type: 'success',
        title: 'Revenue Target Achieved',
        message: 'Daily revenue target reached at 8:45 PM',
        time: '1 hour ago'
      });
    }
    
    if (randomInRange(0, 1) > 0.8) {
      alerts.push({
        type: 'info',
        title: 'Peak Hour Starting',
        message: 'Dinner rush expected in 30 minutes',
        time: '30 minutes ago'
      });
    }
    
    return alerts;
  }

  // Multi-restaurant comparison (for admin)
  generateRestaurantComparison() {
    return RESTAURANTS.map(restaurant => {
      const revenue = randomInt(2100000, 4200000); // Revenue in INR (21-42 Lakhs per week)
      const orders = randomInt(1500, 3500);
      
      return {
        id: restaurant.id,
        name: restaurant.name,
        location: restaurant.location,
        revenue,
        revenueChange: randomInRange(-10, 20).toFixed(1),
        orders,
        ordersChange: randomInRange(-5, 15).toFixed(1),
        avgOrderValue: Math.round(revenue / orders),
        profitMargin: randomInRange(35, 45).toFixed(1), // 35-45% profit margin
        customerRating: randomInRange(4.0, 4.9).toFixed(1),
        efficiency: randomInRange(80, 98).toFixed(0),
        alerts: randomInt(0, 3)
      };
    });
  }
}

export default new MockDataGenerator();
export { RESTAURANTS, MENU_ITEMS, STAFF };

